import { useState, useCallback, useMemo } from "react";
import { previewAccessRuleCoverage } from "@/helpers/apis";
import { AccessRuleCoveragePreviewResponse } from "./types";
import { useDebounce } from "@/helpers/hooks";
import { AccessRulesFormValues } from "./types";
import { formatAccessRulePayload } from "./accessRuleHelpers";

export const useAccessRuleCoverage = (watchedRules: AccessRulesFormValues["rules"]) => {
  const debouncedRules = useDebounce(watchedRules, 400);
  const formattedRules = useMemo(() => formatAccessRulePayload(debouncedRules), [debouncedRules]);
  const shouldPreviewCoverage = formattedRules.some(rule => rule.entity?.trim());
  const [coverageSummary, setCoverageSummary] = useState<AccessRuleCoveragePreviewResponse | null>(
    null
  );
  const [isCoverageLoading, setIsCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState<string | null>(null);

  const refreshCoverage = useCallback(
    async (rulesToUse?: typeof formattedRules) => {
      const rules = rulesToUse || formattedRules;

      setIsCoverageLoading(true);
      setCoverageError(null);
      try {
        const response = await previewAccessRuleCoverage({
          rules: rules,
        });
        setCoverageSummary(response ?? null);
      } catch (error) {
        console.error(error);
        setCoverageSummary(null);
        setCoverageError("Unable to refresh asset visibility right now.");
      } finally {
        setIsCoverageLoading(false);
      }
    },
    [formattedRules]
  );

  return {
    coverageSummary,
    isCoverageLoading,
    coverageError,
    shouldPreviewCoverage,
    refreshCoverage,
    formattedRules,
  };
};
